// SEO and meta tag utilities
import { Helmet } from 'react-helmet-async';

// Page-specific SEO data
export const seoData = {
  home: {
    title: "I Need Numbers \u2014 The Business Platform for Real Estate Agents",
    description: "Track P&L, commission cap, and goals. Get AI coaching from your own numbers. Share branded calculators and PDFs with buyers and sellers \u2014 all in one app.",
    keywords: "real estate agent software, real estate business platform, commission cap tracker, P&L tracker for agents, AI coach real estate, real estate agent app",
    canonicalUrl: "https://ineednumbers.com/",
    type: "website"
  },
  calculator: {
    title: "Free Investor Deal Analyzer \u2014 Cap Rate, CoC, DSCR, IRR",
    description: "Free public calculator for cap rate, cash-on-cash, DSCR, and IRR. No signup required. See what the full Investor Deal Analyzer looks like inside I Need Numbers.",
    keywords: "investor deal analyzer, cap rate calculator, cash on cash calculator, DSCR calculator, IRR calculator, real estate investment calculator",
    canonicalUrl: "https://ineednumbers.com/calculator",
    type: "website"
  },
  tools: {
    title: "Real Estate Agent Tools & Client-Facing Calculators",
    description: "Every tool an agent needs in one place: commission splits, seller net sheets, affordability, closing timelines, and investor analysis \u2014 all with branded PDF output.",
    keywords: "real estate agent tools, commission split calculator, seller net sheet, affordability calculator, closing date calculator, branded PDF reports",
    canonicalUrl: "https://ineednumbers.com/tools",
    type: "website"
  },
  pricing: {
    title: "Pricing \u2014 I Need Numbers for Real Estate Agents",
    description: "Free tools, a Pro plan with the Fairy AI Coach and branded PDFs, and a ready-to-go trial. One subscription for your entire real estate business.",
    keywords: "real estate software pricing, I Need Numbers pricing, agent tools subscription, AI coach subscription",
    canonicalUrl: "https://ineednumbers.com/pricing",
    type: "website"
  },
  commissionSplit: {
    title: "Commission Split Calculator for Real Estate Agents",
    description: "Know your real take-home before the deal is signed. Calculates splits, caps, broker fees, and referrals \u2014 then saves a branded PDF for your records.",
    keywords: "commission split calculator, real estate commission calculator, agent take-home calculator, brokerage split calculator",
    canonicalUrl: "https://ineednumbers.com/tools/commission-split",
    type: "website"
  },
  netSheet: {
    title: "Seller Net Sheet Calculator \u2014 Win the Listing With Clarity",
    description: "Show sellers their true walk-away number at the listing appointment. Runs closing costs, payoffs, commissions, and title fees \u2014 then exports a branded PDF.",
    keywords: "seller net sheet, net sheet calculator, listing appointment tool, seller closing cost calculator, branded net sheet PDF",
    canonicalUrl: "https://ineednumbers.com/tools/net-sheet",
    type: "website"
  },
  affordability: {
    title: "Affordability Calculator for Real Estate Agents & Buyers",
    description: "Pre-qualify every buyer before the first showing. Calculates max home price from income, debts, and rates \u2014 share a public link buyers can open without signup.",
    keywords: "affordability calculator, home affordability calculator, buyer pre-qualification, DTI calculator, shareable affordability calculator",
    canonicalUrl: "https://ineednumbers.com/tools/affordability",
    type: "website"
  },
  closingDate: {
    title: "Closing Date Calculator \u2014 Every Deadline in One Timeline",
    description: "Build a full transaction timeline from the contract date \u2014 inspection, financing, appraisal, title, closing. Share one link with buyer and seller.",
    keywords: "closing date calculator, closing timeline, real estate deadlines, contract to close calculator, transaction timeline",
    canonicalUrl: "https://ineednumbers.com/tools/closing-date",
    type: "website"
  },
  blog: {
    title: "Blog \u2014 Real Estate Business Insights | I Need Numbers",
    description: "Practical posts on commission structures, lead sources, buyer and seller conversations, AI for agents, and the numbers behind a healthy real estate business.",
    keywords: "real estate blog, agent business tips, commission guide, lead source ROI, real estate AI coaching",
    canonicalUrl: "https://ineednumbers.com/blog",
    type: "website"
  },
  aiCoach: {
    title: "Fairy AI Coach \u2014 Real Estate Coaching From Your Numbers",
    description: "A coach that actually reads your P&L, goals, and activity \u2014 then tells you exactly what to focus on this week. No generic tips, no broker meeting required.",
    keywords: "real estate AI coach, agent coaching, Fairy AI Coach, data-driven real estate coaching, agent business coach",
    canonicalUrl: "https://ineednumbers.com/features/ai-coach",
    type: "website"
  },
  mortgageCalculator: {
    title: "Mortgage Calculator for Real Estate Agents | I Need Numbers",
    description: "Run payment and amortization numbers in seconds, right alongside every buyer conversation. Built for agents \u2014 no bank website, no waiting for a quote.",
    keywords: "mortgage calculator, real estate agent mortgage tool, payment calculator, amortization calculator, buyer payment calculator",
    canonicalUrl: "https://ineednumbers.com/features/mortgage-calculator",
    type: "website"
  },
  netSheetFeature: {
    title: "Seller Net Sheet Calculator \u2014 Win the Listing With Clarity",
    description: "Show sellers their true walk-away number at the listing appointment. Runs closing costs, payoffs, commissions, and title fees \u2014 then exports a branded PDF.",
    keywords: "seller net sheet feature, listing appointment tool, net sheet PDF, branded seller report",
    canonicalUrl: "https://ineednumbers.com/features/net-sheet",
    type: "website"
  },
  commissionCalculatorFeature: {
    title: "Commission Split Calculator for Real Estate Agents",
    description: "Know your real take-home before the deal is signed. Calculates splits, caps, broker fees, and referrals \u2014 then saves a branded PDF for your records.",
    keywords: "commission calculator feature, agent take-home, commission split, cap structure calculator",
    canonicalUrl: "https://ineednumbers.com/features/commission-calculator",
    type: "website"
  },
  closingDateFeature: {
    title: "Closing Date Calculator \u2014 Every Deadline in One Timeline",
    description: "Build a full transaction timeline from the contract date \u2014 inspection, financing, appraisal, title, closing. Share one link with buyer and seller.",
    keywords: "closing date calculator feature, transaction timeline tool, deadline tracker, closing timeline PDF",
    canonicalUrl: "https://ineednumbers.com/features/closing-date",
    type: "website"
  },
  dealAnalyzer: {
    title: "Investor Deal Analyzer \u2014 Cap Rate, CoC, DSCR, IRR in Minutes",
    description: "Speak the investor\u2019s language. Get instant cash flow, ROI, and holding-cost analysis \u2014 plus a branded investor packet you can send before the first showing.",
    keywords: "investor deal analyzer, real estate investment analysis, cap rate, cash on cash, DSCR, IRR, investor PDF",
    canonicalUrl: "https://ineednumbers.com/features/deal-analyzer",
    type: "website"
  },
  pnlTracker: {
    title: "P&L Tracker for Real Estate Agents | I Need Numbers",
    description: "Log income and expenses by deal and lead source as they happen. Tax season becomes a download \u2014 and you finally see which lead sources actually make money.",
    keywords: "real estate P&L tracker, agent expense tracker, lead source ROI, real estate bookkeeping, income tracker for agents",
    canonicalUrl: "https://ineednumbers.com/features/pnl-tracker",
    type: "website"
  },
  capReport: {
    title: "Commission Cap Tracker \u2014 Know Your Dollars-to-Cap Anytime",
    description: "Stop getting surprised by your brokerage cap. Track progress live, see dollars-to-cap, and plan for the take-home jump instead of finding out in December.",
    keywords: "commission cap tracker, brokerage cap calculator, dollars to cap, agent cap progress",
    canonicalUrl: "https://ineednumbers.com/features/cap-report",
    type: "website"
  },
  actionTracker: {
    title: "Action Tracker for Agents \u2014 Log the Activity That Drives Deals",
    description: "Closings are a lagging indicator \u2014 calls, appointments, and follow-ups are what predict next quarter. Log activity daily and let the AI Coach hold the line.",
    keywords: "agent action tracker, activity tracker, daily habits real estate, agent accountability app, leading indicators real estate",
    canonicalUrl: "https://ineednumbers.com/features/action-tracker",
    type: "website"
  }
};

// Generate structured data for different content types
export const generateStructuredData = (pageType, additionalData = {}) => {
  switch (pageType) {
    case 'calculator':
      return {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "Real Estate Investment Calculator | I Need Numbers",
        "applicationCategory": "BusinessApplication",
        "operatingSystem": "Web",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD"
        },
        "provider": {
          "@type": "Organization",
          "name": "I Need Numbers",
          "url": "https://ineednumbers.com"
        }
      };

    case 'commissionSplit':
      return {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "Commission Split Calculator | I Need Numbers",
        "applicationCategory": "BusinessApplication",
        "operatingSystem": "Web",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD"
        }
      };

    case 'netSheet':
      return {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "Seller Net Sheet Calculator | I Need Numbers",
        "applicationCategory": "BusinessApplication",
        "operatingSystem": "Web",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD"
        }
      };

    case 'affordability':
      return {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "Mortgage Affordability Calculator | I Need Numbers",
        "applicationCategory": "BusinessApplication",
        "operatingSystem": "Web",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD"
        }
      };

    case 'closingDate':
      return {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "Closing Date Calculator | I Need Numbers",
        "applicationCategory": "BusinessApplication",
        "operatingSystem": "Web",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD"
        }
      };
    
    case 'tools':
      return {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Real Estate Agent Tools",
        "url": "https://ineednumbers.com/tools",
        "mainEntity": {
          "@type": "ItemList",
          "itemListElement": [
            { "@type": "SoftwareApplication", "name": "Commission Split Calculator", "applicationCategory": "BusinessApplication" },
            { "@type": "SoftwareApplication", "name": "Seller Net Sheet Calculator", "applicationCategory": "BusinessApplication" },
            { "@type": "SoftwareApplication", "name": "Affordability Calculator", "applicationCategory": "BusinessApplication" },
            { "@type": "SoftwareApplication", "name": "Closing Date Calculator", "applicationCategory": "BusinessApplication" }
          ]
        }
      };
      
    case 'organization':
      return {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "I Need Numbers",
        "url": "https://ineednumbers.com",
        "logo": "https://customer-assets.emergentagent.com/job_agent-portal-27/artifacts/azdcmpew_Logo_with_brown_background-removebg-preview.png",
        "description": "Professional real estate calculators and tools for agents",
        "sameAs": ["https://ineednumbers.com"],
        "contactPoint": {
          "@type": "ContactPoint",
          "contactType": "Customer Service",
          "url": "https://ineednumbers.com/support"
        }
      };
      
    default:
      return {
        "@context": "https://schema.org",
        "@type": "WebApplication",
        "name": "I Need Numbers",
        "url": "https://ineednumbers.com",
        "description": "Professional real estate calculators and tools for agents",
        "applicationCategory": "BusinessApplication",
        "operatingSystem": "Web",
        "provider": {
          "@type": "Organization",
          "name": "I Need Numbers",
          "url": "https://ineednumbers.com"
        }
      };
  }
};

// FAQ schemas for specific pages
export const generateFAQSchema = (pageType) => {
  const faqs = {
    home: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is I Need Numbers?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "I Need Numbers is a real estate analytics platform with professional calculators for agents \u2014 including cap rate, cash-on-cash return, commission split, seller net sheet, and affordability tools. Generate branded investor PDFs in minutes."
          }
        },
        {
          "@type": "Question",
          "name": "How much does I Need Numbers cost?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "I Need Numbers offers a Pro plan with full access to all calculators, branded investor PDFs, unlimited deals, and advanced agent tools. Visit our pricing page for current pricing."
          }
        },
        {
          "@type": "Question",
          "name": "What calculators are included?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "The platform includes cap rate, cash-on-cash return, DSCR, IRR, commission split, seller net sheet, affordability, and closing date calculators \u2014 all with professional PDF export."
          }
        }
      ]
    },
    commissionSplit: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How do real estate commission splits work?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "In a real estate transaction, the total commission (typically 5-6% of the sale price) is split between the listing and buyer's agents. Each agent then splits their portion with their brokerage according to their agreement, which can range from 50/50 to 90/10 depending on experience and volume."
          }
        },
        {
          "@type": "Question",
          "name": "What is a typical brokerage split?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Brokerage splits vary widely. New agents often start at 50/50 or 60/40, while experienced agents may negotiate 70/30 or 80/20. Some brokerages use a commission cap model where agents keep 100% after reaching a threshold."
          }
        },
        {
          "@type": "Question",
          "name": "How do I calculate my take-home commission?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Use our free commission split calculator to enter the sale price, total commission rate, your split percentage, and any referral or franchise fees. The calculator instantly shows your net take-home amount after all deductions."
          }
        }
      ]
    },
    netSheet: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is a seller net sheet?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "A seller net sheet is a document that estimates the proceeds a seller will receive from a real estate transaction after all costs are deducted, including agent commissions, closing costs, title fees, and any outstanding mortgage balance."
          }
        },
        {
          "@type": "Question",
          "name": "What costs are included in a seller net sheet?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Common costs include real estate commissions, title insurance, transfer taxes, prorated property taxes, HOA fees, mortgage payoff, recording fees, and any seller concessions or repair credits agreed upon in the contract."
          }
        }
      ]
    },
    affordability: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How much house can I afford?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Home affordability depends on your income, debts, down payment, interest rate, and local taxes and insurance. A common guideline is that your monthly housing costs should not exceed 28% of your gross monthly income. Our calculator factors in all these variables to give you an accurate estimate."
          }
        },
        {
          "@type": "Question",
          "name": "What is the debt-to-income ratio for a mortgage?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "The debt-to-income (DTI) ratio compares your monthly debt payments to your gross monthly income. Most lenders require a DTI of 43% or less for conventional loans, though FHA loans may allow up to 50% in some cases."
          }
        }
      ]
    },
    closingDate: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How long does it take to close on a house?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "The typical closing timeline is 30-45 days from contract acceptance for financed purchases. Cash purchases can close in as little as 7-14 days. Our closing date calculator maps out every milestone from contract to closing."
          }
        },
        {
          "@type": "Question",
          "name": "What are the key dates in a real estate closing timeline?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Key milestones include the due diligence period, earnest money deposit deadline, appraisal, home inspection, loan approval, title search, final walkthrough, and closing day. Each date is typically calculated from the contract acceptance date."
          }
        }
      ]
    },
    calculator: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is a cap rate in real estate?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Cap rate (capitalization rate) is the ratio of a property's net operating income (NOI) to its purchase price, expressed as a percentage. It helps investors compare the relative value of different investment properties. A higher cap rate generally indicates higher potential return but also higher risk."
          }
        },
        {
          "@type": "Question",
          "name": "How do you calculate cash-on-cash return?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Cash-on-cash return measures the annual pre-tax cash flow relative to the total cash invested. Divide the annual net cash flow by your total out-of-pocket investment (down payment, closing costs, repairs). This metric is especially useful for evaluating leveraged real estate investments."
          }
        },
        {
          "@type": "Question",
          "name": "What is DSCR and why does it matter?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "DSCR (Debt Service Coverage Ratio) measures whether a property's income can cover its debt payments. A DSCR above 1.0 means the property generates enough income to cover the mortgage. Lenders typically require a DSCR of 1.2-1.25 for investment property loans."
          }
        }
      ]
    }
  };

  return faqs[pageType] || null;
};

// SEO component wrapper
export const SEOHelmet = ({ 
  pageKey, 
  customTitle, 
  customDescription, 
  customKeywords,
  customCanonical,
  customOgImage,
  additionalMeta = [] 
}) => {
  const seoInfo = seoData[pageKey] || seoData.home;
  const title = customTitle || seoInfo.title;
  const description = customDescription || seoInfo.description;
  const keywords = customKeywords || seoInfo.keywords;
  const canonical = customCanonical || seoInfo.canonicalUrl;
  const ogImage = customOgImage || seoInfo.ogImage || 'https://ineednumbers.com/og/default.png';
  
  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonical} />
      
      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={seoInfo.type || 'website'} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="I Need Numbers" />
      
      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      
      {/* Additional Meta Tags */}
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" />
      <meta name="author" content="I Need Numbers" />
      <meta name="language" content="English" />
      <meta name="geo.region" content="US" />
      <meta name="theme-color" content="#2FA163" />
      
      {/* Additional custom meta tags */}
      {additionalMeta.map((meta, index) => (
        <meta key={index} {...meta} />
      ))}
    </Helmet>
  );
};

// Renders JSON-LD structured data in the body (works with react-snap prerendering)
export const StructuredData = ({ data }) => {
  if (!data) return null;
  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }} />
  );
};
